import { Button, Input } from '../components/admin/ui';
import { useEffect, useState } from 'react';
import {
  Plus,
  Trash2,
  Pencil,
  X,
  Loader2,
  Shield,
  Star,
  Clock,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { adminApi, SlaPolicy } from '../lib/admin-api';

const priorities = ['low', 'normal', 'high', 'urgent'] as const;

const defaultTargets = {
  first_response: { low: 14400, normal: 3600, high: 1800, urgent: 300 },
  resolution: { low: 604800, normal: 86400, high: 28800, urgent: 7200 },
};

function formatSeconds(seconds: number): string {
  if (seconds >= 86400) return `${Math.round(seconds / 86400)}d`;
  if (seconds >= 3600) return `${Math.round(seconds / 3600)}h`;
  if (seconds >= 60) return `${Math.round(seconds / 60)}m`;
  return `${seconds}s`;
}

function parseToSeconds(value: string): number {
  const num = parseInt(value, 10);
  if (isNaN(num)) return 0;
  return num;
}

export function SlaPage() {
  const { profile } = useAuth();
  const [policies, setPolicies] = useState<SlaPolicy[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formIsDefault, setFormIsDefault] = useState(false);
  const [formFirstResponse, setFormFirstResponse] = useState(defaultTargets.first_response);
  const [formResolution, setFormResolution] = useState(defaultTargets.resolution);
  const [formBreachActions, setFormBreachActions] = useState({
    notify_supervisor: true,
    auto_escalate: false,
    increase_priority: true,
  });
  const [formError, setFormError] = useState('');

  useEffect(() => {
    fetchPolicies();
  }, [profile?.organization_id]);

  async function fetchPolicies() {
    if (!profile?.organization_id) return;
    setLoading(true);
    const data = await adminApi.getSlaPolicies(profile.organization_id);
    setPolicies(data);
    setLoading(false);
  }

  const resetForm = () => {
    setFormName('');
    setFormDescription('');
    setFormIsDefault(false);
    setFormFirstResponse(defaultTargets.first_response);
    setFormResolution(defaultTargets.resolution);
    setFormBreachActions({ notify_supervisor: true, auto_escalate: false, increase_priority: true });
    setFormError('');
    setShowForm(false);
    setEditingId(null);
  };

  const handleEdit = (policy: SlaPolicy) => {
    setEditingId(policy.id);
    setFormName(policy.name);
    setFormDescription(policy.description || '');
    setFormIsDefault(policy.is_default);
    setFormFirstResponse(policy.first_response_target as typeof defaultTargets.first_response);
    setFormResolution(policy.resolution_target as typeof defaultTargets.resolution);
    setFormBreachActions(policy.breach_actions as typeof formBreachActions);
    setFormError('');
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formName.trim()) {
      setFormError('Name is required');
      return;
    }

    setSaving(true);
    setFormError('');

    const policyData = {
      name: formName.trim(),
      description: formDescription.trim() || null,
      is_default: formIsDefault,
      first_response_target: formFirstResponse,
      resolution_target: formResolution,
      business_hours: {},
      breach_actions: formBreachActions,
      is_active: true,
    };

    if (editingId) {
      const success = await adminApi.updateSlaPolicy(editingId, policyData);
      if (!success) {
        setFormError('Failed to update policy');
        setSaving(false);
        return;
      }
    } else {
      if (!profile?.organization_id) return;
      const result = await adminApi.createSlaPolicy(profile.organization_id, policyData);
      if (!result) {
        setFormError('Failed to create policy');
        setSaving(false);
        return;
      }
    }

    setSaving(false);
    resetForm();
    fetchPolicies();
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this SLA policy?')) return;
    await adminApi.deleteSlaPolicy(id);
    fetchPolicies();
  };

  const handleSetDefault = async (id: string) => {
    // Unset all other defaults first
    for (const p of policies) {
      if (p.is_default && p.id !== id) {
        await adminApi.updateSlaPolicy(p.id, { is_default: false });
      }
    }
    await adminApi.updateSlaPolicy(id, { is_default: true });
    fetchPolicies();
  };

  return (
    <div className="support-page">
      <div className="support-page-heading">
        <div>
          <h1 className="support-page-title">SLA policies</h1>
          <p className="text-muted-foreground mt-1">
            Set response and resolution time targets per priority level
          </p>
        </div>
        <Button variant="ghost"
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary transition-colors text-sm font-medium"
        >
          <Plus className="w-4 h-4" />
          New Policy
        </Button>
      </div>

      {/* Create/Edit Form */}
      {showForm && (
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">
              {editingId ? 'Edit Policy' : 'New SLA Policy'}
            </h3>
            <Button aria-label="Close" size="iconSm" variant="ghost" onClick={resetForm} className="text-muted-foreground hover:text-muted-foreground">
              <X className="w-5 h-5" />
            </Button>
          </div>

          {formError && (
            <div className="mb-4 p-3 bg-destructive/10 border border-destructive/30 rounded-lg text-sm text-error-text">
              {formError}
            </div>
          )}

          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="slapage-1" className="block text-sm font-medium text-foreground mb-1">Name</label>
                <Input id="slapage-1"
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="Standard SLA"
                  className="w-full border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-ring focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Description <span className="text-muted-foreground font-normal">(optional)</span>
                </label>
                <Input
                  type="text"
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  placeholder="Default SLA for all conversations"
                  className="w-full border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-ring focus:border-primary"
                />
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formIsDefault}
                onChange={(e) => setFormIsDefault(e.target.checked)}
                className="rounded border-border text-link focus:ring-ring"
              />
              <span className="text-sm text-foreground">Set as default policy for new conversations</span>
            </label>

            {/* First Response Targets */}
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                First Response Target (seconds)
              </h4>
              <div className="grid grid-cols-4 gap-3">
                {priorities.map((p) => (
                  <div key={p}>
                    <label htmlFor={`slapage-2-${p}`} className="block text-xs text-muted-foreground capitalize mb-1">{p}</label>
                    <Input id={`slapage-2-${p}`}
                      type="number"
                      value={formFirstResponse[p]}
                      onChange={(e) =>
                        setFormFirstResponse({ ...formFirstResponse, [p]: parseToSeconds(e.target.value) })
                      }
                      className="w-full border border-border rounded-md px-2 py-1.5 text-sm focus:ring-2 focus:ring-ring"
                    />
                    <span className="text-[10px] text-muted-foreground">{formatSeconds(formFirstResponse[p])}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Resolution Targets */}
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Resolution Target (seconds)
              </h4>
              <div className="grid grid-cols-4 gap-3">
                {priorities.map((p) => (
                  <div key={p}>
                    <label htmlFor={`slapage-3-${p}`} className="block text-xs text-muted-foreground capitalize mb-1">{p}</label>
                    <Input id={`slapage-3-${p}`}
                      type="number"
                      value={formResolution[p]}
                      onChange={(e) =>
                        setFormResolution({ ...formResolution, [p]: parseToSeconds(e.target.value) })
                      }
                      className="w-full border border-border rounded-md px-2 py-1.5 text-sm focus:ring-2 focus:ring-ring"
                    />
                    <span className="text-[10px] text-muted-foreground">{formatSeconds(formResolution[p])}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Breach Actions */}
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-2">On SLA Breach</h4>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formBreachActions.notify_supervisor}
                    onChange={(e) => setFormBreachActions({ ...formBreachActions, notify_supervisor: e.target.checked })}
                    className="rounded border-border text-link focus:ring-ring"
                  />
                  <span className="text-sm text-foreground">Notify supervisor</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formBreachActions.increase_priority}
                    onChange={(e) => setFormBreachActions({ ...formBreachActions, increase_priority: e.target.checked })}
                    className="rounded border-border text-link focus:ring-ring"
                  />
                  <span className="text-sm text-foreground">Increase priority</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formBreachActions.auto_escalate}
                    onChange={(e) => setFormBreachActions({ ...formBreachActions, auto_escalate: e.target.checked })}
                    className="rounded border-border text-link focus:ring-ring"
                  />
                  <span className="text-sm text-foreground">Auto-escalate to next available agent</span>
                </label>
              </div>
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <Button variant="ghost"
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary disabled:bg-primary transition-colors text-sm font-medium flex items-center gap-2"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {editingId ? 'Update' : 'Create'}
            </Button>
            <Button variant="ghost"
              onClick={resetForm}
              className="px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-border transition-colors text-sm font-medium"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Policies list */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-link" />
        </div>
      ) : policies.length === 0 ? (
        <div className="text-center py-12 bg-card rounded-xl border border-border">
          <Shield className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">No SLA policies yet</p>
          <p className="text-sm text-muted-foreground mt-1">
            Create policies to enforce response and resolution times
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {policies.map((policy) => (
            <div
              key={policy.id}
              className={`bg-card rounded-lg border border-border p-5 ${
                !policy.is_active ? 'opacity-60' : ''
              }`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium text-foreground">{policy.name}</h3>
                    {policy.is_default && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold bg-warning text-warning-foreground rounded-full">
                        <Star className="w-3 h-3" /> Default
                      </span>
                    )}
                    {!policy.is_active && (
                      <span className="px-2 py-0.5 text-[10px] font-medium bg-muted text-muted-foreground rounded-full">
                        Inactive
                      </span>
                    )}
                  </div>
                  {policy.description && (
                    <p className="text-sm text-muted-foreground mt-1">{policy.description}</p>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {!policy.is_default && (
                    <Button size="iconSm" variant="ghost"
                      onClick={() => handleSetDefault(policy.id)}
                      className="p-1.5 text-muted-foreground hover:text-warning-foreground hover:bg-warning rounded transition-colors"
                      title="Set as default"
                    >
                      <Star className="w-4 h-4" />
                    </Button>
                  )}
                  <Button aria-label="Edit item" size="iconSm" variant="ghost"
                    onClick={() => handleEdit(policy)}
                    className="p-1.5 text-muted-foreground hover:text-link hover:bg-primary/15 rounded transition-colors"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button aria-label="Delete item" size="iconSm" variant="ghost"
                    onClick={() => handleDelete(policy.id)}
                    className="p-1.5 text-muted-foreground hover:text-error-text hover:bg-destructive/10 rounded transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Targets table */}
              <div className="mt-4 grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">First Response</h4>
                  <div className="grid grid-cols-4 gap-2">
                    {priorities.map((p) => (
                      <div key={p} className="text-center">
                        <div className="text-[10px] text-muted-foreground capitalize">{p}</div>
                        <div className="text-sm font-medium text-foreground">
                          {formatSeconds((policy.first_response_target as Record<string, number>)[p] || 0)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Resolution</h4>
                  <div className="grid grid-cols-4 gap-2">
                    {priorities.map((p) => (
                      <div key={p} className="text-center">
                        <div className="text-[10px] text-muted-foreground capitalize">{p}</div>
                        <div className="text-sm font-medium text-foreground">
                          {formatSeconds((policy.resolution_target as Record<string, number>)[p] || 0)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
